import type { OkkQuestionsConfig } from '@/data/okkForm';

export type ID = string;

/**
 * Демо-«текущий пользователь» (пока вход не сделан — Этап 1).
 * По нему работает тоггл «Показывать только где назначен» на дашборде.
 */
export const DEMO_CURRENT_USER_NAME = 'Валерий Паршуткин';

// ── Справочники ──────────────────────────────────────────

export type Currency = 'KZT' | 'UZS' | 'AZN' | 'KGS';
export const CURRENCY_LABEL: Record<Currency, string> = {
  KZT: 'тг',
  UZS: 'сум',
  AZN: 'манат',
  KGS: 'сом',
};

export type Department = 'sales' | 'event';
export const DEPARTMENT_LABEL: Record<Department, string> = {
  sales: 'Отдел продаж',
  event: 'Отдел ивента',
};

/** Состояние синхронизации с Bitrix24. */
export type SyncState = 'local' | 'syncing' | 'synced' | 'dirty' | 'conflict' | 'error';
export interface BitrixRef {
  /** ID сущности в Bitrix24 (string как в REST API). */
  bitrix_id?: string;
  /** Что именно: CRM_DEAL / CRM_CONTACT / USER / SECTION / DEPARTMENT и т.д. */
  bitrix_entity?: string;
  /** Состояние последней синхронизации. */
  sync_state?: SyncState;
  /** Когда последний раз успешно синкали. */
  synced_at?: number;
  /** Сообщение об ошибке (если sync_state = error). */
  sync_error?: string;
}

export interface Branch extends BitrixRef {
  id: ID;
  name: string;
  city?: string;
  color: string;
  tz_offset_minutes: number;
  /** IANA-пояс филиала (Asia/Almaty, Asia/Baku…) — по нему считаются «завтра» и часы рассылок. */
  timezone?: string;
  /** Значение поля «Филиал» в Битриксе (enum-id): по нему сделка попадает в этот филиал. */
  bitrix_enum_id?: string;
  /** Telegram-группа филиала (куда уходит сводка). Привязывается кодом из самой группы. */
  telegram_group_chat_id?: string;
  /** Признак «группа привязана» — вычисляется сервером. */
  telegram_linked?: boolean;
  /** Соответствие подразделению Bitrix (DEPARTMENT). Заполняется при синхронизации. */
  bitrix_department_id?: number;
  /** Раздел календаря Bitrix (SECTION) для слотов этого филиала. */
  bitrix_calendar_section_id?: number;
}

export interface Employee extends BitrixRef {
  id: ID;
  full_name: string;
  branch_id?: ID | null;
  /** Должность сотрудника (Ведущий/Диджей/…); при регистрации берётся из приглашения. */
  role?: string;
  /** Должность из Битрикса (WORK_POSITION) — свободный текст. */
  position?: string;
  phone?: string;
  email?: string;
  username?: string;        // ник (@daik203) — для отображения в карточке/персонале
  is_active: boolean;
  /** Bitrix USER ID (для ASSIGNED_BY_ID). */
  bitrix_user_id?: number;
  /**
   * Роль доступа для входа через Битрикс (id из access_roles). Задаётся вручную
   * супер-админом. NULL/undefined → входа нет (только справочник). Применяется при
   * ПЕРВОМ входе через Битрикс — минуя проверку должности.
   */
  login_access_role?: string;
  /**
   * Уровень сотрудника (1/2/3) — РАЗДЕЛЬНО по ролям: от него зависит ставка за игру
   * (расчёт зарплаты). Один человек может быть, например, 1-м уровнем как ведущий и
   * 3-м как диджей — роли считаются независимо. null/не задан — ставка не определена.
   * Правит тот, кто может редактировать карточку сотрудника (управляющий/руководитель).
   */
  level_host?: 1 | 2 | 3 | null;
  level_dj?: 1 | 2 | 3 | null;
  /** Уровень как соведущего/хостес (решение 02.11.2026) — своя ставка, своя тарифная ось. */
  level_sovedushiy?: 1 | 2 | 3 | null;
  level_hostes?: 1 | 2 | 3 | null;
  /** Фото — не своё поле карточки, а то, что сотрудник сам загрузил себе в «Профиле». */
  avatar?: string | null;
  /** Дата рождения ('YYYY-MM-DD') — заполняет сам сотрудник в «Профиле». */
  birth_date?: string | null;
}

/** Должности сотрудника (совпадают с «типами работника» приглашения). */
export const EMPLOYEE_ROLE_OPTIONS = [
  'Руководство', 'Управляющий', 'РОП', 'Менеджер ОП',
  'Ведущий', 'Соведущий', 'Диджей', 'Хостес', 'Водитель', 'Стажёр',
  'Администратор', 'Техник', 'Управленец',
] as const;

export interface Team {
  id: ID;
  name: string;
  color: string;
  branch_id?: ID | null;
  member_ids: ID[];
  is_archived: boolean;
  /** Эмодзи/иконка команды (как в эталоне ShowToday). */
  icon?: string;
  /** Описание команды. */
  description?: string;
  /** Группа команды (для группировки в списках). */
  group?: string;
  /** Явный порядок сортировки в списках/календаре. */
  order?: number;
  /** В Bitrix хранится через UF_CRM_TEAM или отдельную сущность. */
  bitrix_team_field_value?: string;
}

// Источник лида / сделки — соответствует CRM_STATUS (ENTITY_ID = SOURCE).
export interface Source extends BitrixRef {
  id: ID;
  name: string;
  /** STATUS_ID в Bitrix CRM. */
  bitrix_status_id?: string;
}

// Повод (тип события) — кастомное поле UF_CRM_OCCASION (или своё).
export interface Occasion extends BitrixRef {
  id: ID;
  name: string;
  yearly_recurring?: boolean;
}

// Формат игры — UF_CRM_GAME_FORMAT.
export interface GameFormat extends BitrixRef {
  id: ID;
  name: string;
  emoji?: string;   // значок формата (показывается в фильтре/карточке)
  /**
   * Значение ещё существует в Битриксе (поле «Формат игры») — false, если его там убрали.
   * Убранный формат НЕ удаляем из справочника (иначе сломались бы уже сыгранные игры с этим
   * форматом) — просто прячем из списков выбора для НОВЫХ записей. undefined = как true
   * (старые строки до этого поля). Правится только синком, руками не трогаем.
   */
  is_active?: boolean;
}

// Причина отказа — соответствует CRM_STATUS (ENTITY_ID = DEAL_STAGE_FAIL_REASON).
export interface RefusalReason extends BitrixRef {
  id: ID;
  name: string;
}

// ── Карточка клиента / сделка ────────────────────────────

export type DealKind = 'b2c' | 'event';
/** Тип клиента: компания (b2b) / частный (b2c). ОТДЕЛЬНО от kind. Тянется из Битрикса. */
export type ClientType = 'b2b' | 'b2c';
export type DealStatus = 'new' | 'in_work' | 'paid_partial' | 'paid' | 'done' | 'refused';
export const DEAL_STATUS_LABEL: Record<DealStatus, string> = {
  new: 'Новая',
  in_work: 'В работе',
  paid_partial: 'Частично оплачена',
  paid: 'Оплачена',
  done: 'Завершена',
  refused: 'Отказ',
};

export type EventPlace = 'studio' | 'studio_rest' | 'outside';
export const EVENT_PLACE_LABEL: Record<EventPlace, string> = {
  studio: 'Студия',
  studio_rest: 'Студия с комнатой отдыха',
  outside: 'Выезд',
};
/** Значок места проведения (как в эталоне). */
export const EVENT_PLACE_EMOJI: Record<EventPlace, string> = {
  studio: '🏠',
  studio_rest: '🛋️',
  outside: '🚗',
};

/**
 * Статус-воронка заказа (как в выпадающем «Статус заказа» карточки события).
 * Это «человеческие» стадии воронки заказчика; хранятся отдельно от служебного DealStatus,
 * чтобы не ломать дашборд/реестр. На сохранении синхронизируем с DealStatus (см. FUNNEL_TO_DEAL).
 */
export type FunnelStatus =
  | 'prebooking' | 'booking' | 'cancel' | 'done' | 'reschedule' | 'clarify' | 'booking_barter';
export const FUNNEL_STATUS: { key: FunnelStatus; label: string; color: string }[] = [
  { key: 'prebooking',     label: 'Предбронь',          color: '#F2C94C' }, // жёлтый
  { key: 'booking',        label: 'Бронь',              color: '#6FCF97' }, // зелёный
  { key: 'cancel',         label: 'Отмена',             color: '#EB5757' }, // красный
  { key: 'done',           label: 'Проведена',          color: '#9B9B9B' }, // серый
  { key: 'reschedule',     label: 'Перенос',            color: '#F2994A' }, // оранжевый
  { key: 'clarify',        label: '[требует уточнения]', color: '#E0E0E0' }, // светло-серый
  { key: 'booking_barter', label: 'Бронь (бартер)',     color: '#2F80ED' }, // синий
];
export const FUNNEL_BY_KEY = new Map(FUNNEL_STATUS.map((f) => [f.key, f]));
/** Сопоставление воронки → служебный DealStatus (для дашборда/реестра). */
export const FUNNEL_TO_DEAL: Record<FunnelStatus, DealStatus> = {
  prebooking: 'new', booking: 'in_work', booking_barter: 'in_work', reschedule: 'in_work',
  clarify: 'new', done: 'done', cancel: 'refused',
};
/** Обратное сопоставление DealStatus → воронка (когда у сделки ещё нет funnel_status). */
export const DEAL_TO_FUNNEL: Record<DealStatus, FunnelStatus> = {
  new: 'prebooking', in_work: 'booking', paid_partial: 'booking', paid: 'booking',
  done: 'done', refused: 'cancel',
};

/** Дефолт срока «предброни» (мин), если в настройках ещё не задано. */
export const PREBOOKING_HOLD_MINUTES_DEFAULT = 120;

/** Сделка сейчас в статусе «предбронь» (ожидание предоплаты с таймером). */
export function isPrebooking(d: { funnel_status?: FunnelStatus }): boolean {
  return d.funnel_status === 'prebooking';
}
/**
 * «Предбронь» протухла: таймер вышел, предоплата не пришла → слот освобождается
 * (перестаёт занимать команду/время — как отказ). Проверяем по времени, поэтому
 * работает и до того, как фоновая задача сервера пометит сделку в истории.
 */
export function isPrebookingExpired(
  d: { funnel_status?: FunnelStatus; prebooking_expires_at?: number | null; prebooking_expired?: boolean },
  now: number = Date.now(),
): boolean {
  if (!isPrebooking(d)) return false;
  if (d.prebooking_expired) return true; // помечено фоновой задачей сервера
  return d.prebooking_expires_at != null && d.prebooking_expires_at < now;
}
/** Сколько миллисекунд осталось до истечения предброни (0, если срока нет/истёк). */
export function prebookingMsLeft(
  d: { funnel_status?: FunnelStatus; prebooking_expires_at?: number | null },
  now: number = Date.now(),
): number {
  if (!isPrebooking(d) || d.prebooking_expires_at == null) return 0;
  return Math.max(0, d.prebooking_expires_at - now);
}

/** Экран на площадке (как в эталоне ShowToday). */
export type ScreenType = 'none' | 'led' | 'tv' | 'projector';
export const SCREEN_TYPE_LABEL: Record<ScreenType, string> = {
  none: 'отсутствует',
  led: 'LED-экран',
  tv: 'ТВ',
  projector: 'Проектор',
};

/** Чек оплаты — файл (имя + base64-data в MVP, или URL после интеграции с хранилищем) */
export interface PaymentReceipt {
  id: ID;
  filename: string;
  mime?: string;
  size?: number;
  data_url?: string;  // dataURL для ручной загрузки (оффлайн-режим)
  /**
   * Тело файла есть на сервере, но в списке сделок оно не приезжает (только метаданные):
   * карточка догружает его отдельно (см. src/utils/attachments.ts). Пока has_data && !data_url —
   * вложение «грузится»: показываем заглушку, а не пустоту.
   */
  has_data?: boolean;
  /**
   * Ссылка на файл-чек в Битриксе (file-поля «Чек предоплаты»/«Чек остатка»).
   * Когда есть — открываем её в новой вкладке (файл отдаётся по сессии пользователя),
   * а не data_url. У ручных вложений поля нет — они хранят data_url.
   */
  bitrix_url?: string;
  uploaded_at: number;
  /**
   * ID файла в Битриксе, если чек подтянут из CRM (file-поля «Чек предоплаты»/«Чек остатка»).
   * Нужен для дедупа: при повторной синхронизации не добавляем тот же чек второй раз.
   * У чеков, загруженных вручную в приложении, поля нет.
   */
  bitrix_file_id?: string | number;
}

export interface Payment {
  prepayment_amount?: number;          // обязательно при наличии — с чеком
  prepayment_receipts: PaymentReceipt[];
  remainder_amount?: number;
  remainder_receipts: PaymentReceipt[];
}

/** Категория документа в карточке события (как в эталоне ShowToday). */
export type DocCategory = 'photo' | 'blank' | 'certificate' | 'payment';
export const DOC_CATEGORY_LABEL: Record<DocCategory, string> = {
  photo: 'Фото',
  blank: 'Бланк',
  certificate: 'Сертификат',
  payment: 'Оплата',
};

/** Прикреплённый документ сделки с категорией. */
export interface DealDocument {
  id: ID;
  category: DocCategory;
  filename: string;
  mime: string;
  size: number;
  data_url?: string;   // dataURL для оффлайн-режима (Bitrix → disk.file)
  has_data?: boolean;  // тело лежит на сервере и догружается отдельно (см. PaymentReceipt.has_data)
  uploaded_at: number;
}

/** Длительность игры (чистое игровое время). Фиксированный список; '120+' = «120 и больше». */
export type GameDuration = '60' | '90' | '120' | '120+';

// Возраст участников — фиксированный список вариантов (как множественный список в
// Битриксе: поле UF_CRM_1770727235152). Единый источник подписей для формы заявки и
// разбора строки при открытии сделки. ВАЖНО: подписи и разделитель «, » должны
// совпадать с server/src/bitrix/sync.ts (AGE_BY_ENUM + ageTextOf), иначе у сделок,
// импортированных из CRM, галочки не проставятся.
export const PARTICIPANT_AGE_OPTIONS = ['8-12 лет', '13-17 лет', '18-30 лет', '31-45 лет', '45+ лет'] as const;
/** Разделитель вариантов возраста в participant_age_text (тот же, что склеивает импорт). */
export const PARTICIPANT_AGE_SEP = ', ';

// «Язык» игры — множественный выбор (казах.+рус. и т.п.). Хранится человекочит. строкой
// в event.language через тот же разделитель «, », что и возраст. ВАЖНО: подписи и
// разделитель должны совпадать с server/src/bitrix/sync.ts (LANGUAGE_BY_ENUM +
// languageTextOf), иначе у импортированных из CRM сделок галочки не проставятся.
// Порядок — как enum-id в CRM (казах. 13312, рус. 13314, англ. 13316).
export const LANGUAGE_OPTIONS = ['Казахский', 'Русский', 'Английский'] as const;
export const LANGUAGE_SEP = ', ';

export interface EventInfo {
  title?: string;                       // свободное название события («ТИМБИЛДИНГ 2ч+1ч ведение»); если пусто — строим из места+длительности
  occasion_id?: ID;                     // повод (выборка)
  occasion_details?: string;            // подробнее о поводе (для ивента)
  event_date?: number;                  // дата мероприятия
  yearly_recurring?: boolean;           // ежегодное напоминание (если день рождения)
  place?: EventPlace;
  address?: string;
  geo?: { lat: number; lng: number } | null;
  participant_count?: number;
  participant_age_min?: number;
  participant_age_max?: number;
  services?: string[];
  game_format_id?: ID;
  played_before?: boolean;
  duration_minutes?: number;
  team_text?: string;
  start_at?: number;
  end_at?: number;
  // true, если время/дату двигали ВРУЧНУЮ в ERP (Гант/карточка) — тогда синхра из Битрикса
  // НЕ перебивает start_at/end_at/event_date (ручная правка управляющего «прилипает»).
  time_overridden?: boolean;
  // true, если НАЗВАНИЕ игры задали ВРУЧНУЮ в ERP — тогда синхра из Битрикса НЕ перебивает
  // event.title (ручное название «прилипает»). Аналог time_overridden, но для названия.
  title_overridden?: boolean;
  preferred_host?: string;              // предпочтительный ведущий (свободный текст, в Битрикс НЕ уходит)
  language?: string;
  comments?: string;
  order_comment?: string;               // «Комментарий к заказу» из CRM — заметка под конкретный заказ
  // Дополнительные поля из реального шаблона
  uses_invoice_account?: boolean;       // Расчётный счёт да/нет
  // Ожидание — сколько МИНУТ команда простояла на площадке (решение Аяужан 03.11.2026: было
  // просто галочкой да/нет, но доплата = 0.5 часовой ставки за каждый час ожидания, без
  // длительности посчитать её верно нельзя). 0/null/не задано — ожидания не было.
  waiting_minutes?: number | null;
  english_hosting?: boolean;            // Английское ведение — доплата ведущему (галочка для расчёта зарплаты)
  extra_distance_fee?: number;          // Доплата за дальность
  screen_type?: ScreenType;             // Экран на площадке (отсутствует/LED/ТВ/Проектор)
  has_sound?: boolean;                  // Звук на площадке
  responsible_name?: string;            // ПО ФИО (контактное лицо со стороны заказчика)
  responsible_phone?: string;           // ПО Телефон
  emoji?: string;                       // эмодзи на карточке (🎂🚗🎤 и т.д.)
  // Свободно-текстовые значения параметров (как вводят в эталоне: «50%», «разн», «25 человек»)
  prepayment_note?: string;             // Предоплата — как ввели («50%» или «225000»)
  participant_age_text?: string;        // Возраст участников — человекочит. строка из вариантов PARTICIPANT_AGE_OPTIONS через «, » («8-12 лет, 13-17 лет»); старые сделки могут содержать произвольный текст
  participant_count_text?: string;      // Кол-во участников — текст («30-34», «25 человек»)
  game_duration_minutes?: GameDuration;  // Длительность игры (чистое игровое время) — отдельно от слота; '120+' = «120 и больше»
  extra_services_amount?: number;        // Сумма дополнительных услуг
}

/** Тип финансовой операции по сделке. */
export type TxKind = 'income' | 'expense';
export type TxAccount = 'cash' | 'card' | 'qr' | 'invoice';
export const TX_ACCOUNT_LABEL: Record<TxAccount, string> = {
  cash: 'Наличные',
  card: 'Карта',
  qr: 'QR-код',
  invoice: 'Р/с',
};

/** Финансовая операция по сделке (приходы / расходы по статьям). */
export interface DealTransaction {
  id: ID;
  /** Статья: «Оплата за игру», «Приход по бару», «Соведущий», «Диджей», «Ведущий», «Предоплата», … */
  article: string;
  /** Контрагент (для расходов — имя сотрудника/исполнителя) */
  counterparty?: string;
  account: TxAccount;
  amount: number;                       // знак: + приход, − расход
  /** Дата+время «начислено» (план) */
  accrued_at: number;
  accrued_done?: boolean;
  /** Дата+время «проведено» (факт) */
  cleared_at?: number;
  cleared_done?: boolean;
}

export interface RefusalInfo {
  reason_id?: ID;
  comment?: string;
  refused_at?: number;
  /**
   * Отказ пришёл ИЗ БИТРИКСА (сделка провалилась там), а не поставлен руками в ERP.
   * По этой метке синхронизация понимает, чей отказ ей можно снимать: если сделку вернули
   * в Битриксе на рабочую стадию, снимается только «битриксовый» отказ. Отказ, поставленный
   * менеджером в ERP, синхра НЕ отменяет — иначе решение человека молча откатывалось бы.
   */
  from_bitrix?: boolean;
}

export interface Deal extends BitrixRef {
  id: ID;
  // ── Соответствие сущностям Bitrix CRM ──
  // bitrix_id    → ID сделки в Bitrix (CRM_DEAL).
  // bitrix_contact_id → ID контакта (CRM_CONTACT) — создаётся при первой синхронизации.
  // bitrix_company_id → ID компании, если b2b (опционально).
  // bitrix_calendar_event_id → ID события в Calendar (для отображения в календаре Bitrix).
  bitrix_contact_id?: string;
  bitrix_company_id?: string;
  bitrix_calendar_event_id?: string;
  /** Имя менеджера из Битрикса (ASSIGNED_BY_ID) — когда сотрудник ещё не сопоставлен в нашей базе. */
  manager_name?: string;
  /** Комментарий сделки на момент импорта из Битрикса (COMMENTS) — для истории. */
  import_comment?: string;

  // Сделка — основные поля
  number: string;                        // авто-присвоение, → TITLE в Bitrix
  kind: DealKind;                        // b2c (sales) | event → CATEGORY_ID в Bitrix CRM
  client_type?: ClientType;              // тип клиента компания/частный (из Битрикса, ручной выбор побеждает)
  amount?: number;                       // → OPPORTUNITY
  /** Фактическая сумма — вписывает кассир/финансист в ЕРП после игры; из Битрикса НЕ приходит. null = касса ещё не подтвердила. */
  actual_amount?: number | null;
  /**
   * Такси — сумма, которую кассир отправила команде на такси за эту игру. ОТДЕЛЬНО от
   * actual_amount (по просьбе заказчика, встреча 06.10.2026) — иначе расход на такси
   * терялся бы внутри факт.суммы и не отделить его в отчётах/будущей интеграции в Битрикс.
   * Из Битрикса НЕ приходит, вписывает кассир/финансист так же, как фактическую сумму.
   */
  taxi_amount?: number | null;
  /**
   * Агентская продажа (чат 02.11.2026): редкий случай, когда сделку лично продал ведущий/
   * ответственный (responsible_id) — ему положены агентские 5% от фактической суммы, ОТДЕЛЬНО
   * от обычной ставки за работу на игре и независимо от того, работал ли он на ней сам.
   * Ставится вручную — автоопределения нет. Из Битрикса НЕ приходит.
   */
  agent_sale?: boolean;
  currency: Currency;                    // → CURRENCY_ID
  contact_name: string;                  // ФИО → NAME контакта
  contact_phone?: string;                // → PHONE контакта
  branch_id?: ID | null;                 // → UF_CRM_BRANCH (или фильтр по DEPARTMENT)
  /** Ивент-«зеркало»: филиал-город, в календаре которого событие показывается ДОПОЛНИТЕЛЬНО
   *  к своему дом-филиалу (branch_id). Одна карточка сделки видна в обоих. */
  mirror_branch_id?: ID | null;
  /** true — городскую копию (зеркало) убрали из календаря города; оригинал в своём филиале остаётся. */
  mirror_hidden?: boolean;
  responsible_id?: ID | null;            // → ASSIGNED_BY_ID
  source_id?: ID | null;                 // → SOURCE_ID
  type_tag?: string;                     // → TYPE_ID
  status: DealStatus;                    // → STAGE_ID (служебный статус для дашборда/реестра)
  funnel_status?: FunnelStatus;          // статус-воронка карточки (Предбронь/Бронь/Отмена/…)
  /**
   * Срок «предброни» (ts истечения). Пока funnel_status = 'prebooking' — идёт обратный
   * отсчёт до этого времени; если предоплата не пришла — предбронь протухает и освобождает
   * слот. При переходе в «Бронь» (предоплата получена) поле очищается (null).
   */
  prebooking_expires_at?: number | null;
  /**
   * Момент старта предброни (ts). Нужен, чтобы при ручной смене типа клиента пересчитать
   * срок удержания от начала предброни, а не от «сейчас». Проставляет сервер при создании.
   */
  prebooking_started_at?: number | null;
  /** Предбронь истекла (предоплата не пришла в срок) — проставляет фоновая задача сервера. */
  prebooking_expired?: boolean;
  confirmed?: boolean;                   // подтверждено руководителем (галочка в углу карточки)
  confirmed_by?: string;                 // кто подтвердил
  confirmed_at?: number;                 // когда подтвердил
  created_at: number;                    // → DATE_CREATE (read-only из Bitrix)
  taken_at?: number;                     // → BEGINDATE

  payment: Payment;                      // PaymentReceipt → disk.file (вложения)
  documents?: DealDocument[];            // документы по категориям (бланк/сертификат/оплата) → disk.file
  event: EventInfo;                      // → отдельные UF поля + Calendar event
  refusal?: RefusalInfo;                 // → STAGE_ID = LOSE + UF_CRM_REFUSE_REASON

  /** Финансовые операции (приходы/расходы по сделке) — вкладка «Финансы» в карточке. */
  transactions: DealTransaction[];

  /** Участники сделки со стороны исполнителя (ведущий, диджей, оператор и т.п.)
   *  employee_name — СЛЕПОК имени на момент назначения (как у состава команды: он копируется
   *  в игру и живёт своей жизнью). Нужен, чтобы при удалении карточки сотрудника в прошедшей
   *  игре осталось видно, кто на ней работал, а не «неизвестно». Для показа приоритет у
   *  актуального имени из справочника; слепок — запасной вариант. */
  /**
   * is_team_lead — доплата «тимлид» в расчёте зарплаты (фикс. сумма конкретному человеку
   * в смене). Поле есть в данных, но UI-переключателя пока нет — выставляется только
   * напрямую (известный пробел, см. PROJECT.md/заметки по расчёту зарплаты).
   */
  /** addon_keys — свои доплаты (добавленные в «Ставках», помимо фиксированных 7), отмеченные
   *  вручную именно этому человеку на этой игре (решение 03.11.2026, см. PayRatesPage.tsx). */
  staff_assignments: { employee_id: ID; role: string; employee_name?: string; is_team_lead?: boolean; addon_keys?: string[] }[];

  /** История изменений (последние 50). */
  history: {
    ts: number;
    user?: string;
    action: string;
    /** Детализация изменений: что на что поменяли (для таймлайна в карточке). */
    changes?: { label: string; from?: string; to?: string; kind?: 'status' }[];
  }[];

  /** Лента комментариев карточки (сообщения сотрудников: текст и/или вложение с категорией). */
  comments_feed?: {
    id: ID;
    author?: string;
    author_id?: ID;
    created_at: number;
    text?: string;
    // data_url приезжает не сразу: в списке сделок тела вложений не едут (см. has_data).
    attachment?: { filename: string; mime: string; data_url?: string; size?: number; has_data?: boolean };
    category?: DocCategory;   // тег сообщения (Фото/Бланк/Сертификат/Оплата)
    read?: boolean;
  }[];

  /** Ответы анкеты ОКК (опрос качества после мероприятия). Ключ — id вопроса. */
  okk?: {
    answers: Record<string, string>;
    created_by?: string;
    created_at?: number;
    updated_by?: string;
    updated_at?: number;
  };

  team_id?: ID | null;

  /**
   * A3 «Двойные галочки»: у каждой финансовой отметки два слоя —
   *  green (декларация «сделал», ставит менеджер/руководитель/ведущий) →
   *  purple (аудит финансиста, ставится только поверх зелёной).
   * Требуемый набор зависит от client_type: b2c → предоплата+постоплата,
   * b2b → +закрывающие. Обе отметки НЕОБРАТИМЫ. Когда у всех требуемых стоит
   * purple → карточка «финансово проверено» (архив, полный read-only).
   */
  checks?: {
    prepayment?:  { green?: { by: string; at: number }; purple?: { by: string; at: number } };
    postpayment?: { green?: { by: string; at: number }; purple?: { by: string; at: number } };
    closing?:     { green?: { by: string; at: number }; purple?: { by: string; at: number } };
  };

  updated_at: number;
}

/**
 * Значок игры в чипах/карточках: по формату игры (game_format_id → справочник game_formats,
 * у каждого формата своё поле emoji — настраивается под каждый реальный формат заказчика:
 * классика/kids/black/тимбилдинг/ключ/музыкальное лото и т.д.). Формат без своего значка —
 * запасной вариант, ручной event.emoji (старые/демо-данные), иначе обычный 📋.
 */
export function dealEmoji(d: Deal, formats: GameFormat[]): string {
  const f = d.event.game_format_id ? formats.find((x) => x.id === d.event.game_format_id) : null;
  return f?.emoji || d.event.emoji || '📋';
}

/** Ключи финансовых галочек. */
export type FinanceCheckKey = 'prepayment' | 'postpayment' | 'closing';

/**
 * Какие финансовые галочки требуются для «финансово проверено».
 * b2b — три (включая закрывающие документы), b2c/пусто — две.
 */
export function requiredChecks(clientType?: ClientType): FinanceCheckKey[] {
  return clientType === 'b2b'
    ? ['prepayment', 'postpayment', 'closing']
    : ['prepayment', 'postpayment'];
}

/**
 * «Финансово проверено» = у всех требуемых галочек стоит purple (аудит финансиста).
 * Если тип клиента не указан — нельзя понять, нужна ли «закрывающие», поэтому НЕ verified
 * (финансист сначала обязан указать B2B/B2C в карточке).
 */
export function isFinanceVerified(deal: Deal): boolean {
  if (!deal.client_type) return false;
  const req = requiredChecks(deal.client_type);
  const checks = deal.checks ?? {};
  return req.every((k) => !!checks[k]?.purple);
}

/**
 * «Факт» с подстраховкой: если кассир ещё не вписал фактическую сумму (actual_amount == null),
 * берём сумму менеджера (amount), чтобы деньги не пропадали из отчётов/дашборда.
 */
export function effectiveAmount(d: { actual_amount?: number | null; amount?: number | null }): number {
  return d.actual_amount ?? d.amount ?? 0;
}

/** Касса вписала фактическую сумму (иначе — «не подтверждено»). */
export function isFactConfirmed(d: { actual_amount?: number | null }): boolean {
  return d.actual_amount != null;
}

// ── Настройки приложения ─────────────────────────────────

export interface BitrixSettings {
  enabled: boolean;
  /** URL вебхука Bitrix24 вида https://your-portal.bitrix24.ru/rest/USER_ID/TOKEN/ */
  webhook_url?: string;
  /** Альтернатива: OAuth-приложение */
  oauth?: {
    client_id?: string;
    client_secret?: string;
    refresh_token?: string;
    access_token?: string;
    expires_at?: number;
    portal?: string;        // адрес портала
  };
  /** Авто-синхронизация каждые N минут (0 = вручную). */
  auto_sync_interval_min: number;
  /** Последняя успешная синхронизация. */
  last_sync_at?: number;
  /** Только-чтение из Bitrix (не пушим изменения). */
  read_only?: boolean;
}

export interface AppSettings {
  id: 'singleton';
  theme: 'light' | 'dark';
  default_currency: Currency;
  default_branch_id?: ID | null;
  /** Учитывать часовую разницу филиала в дашбордах. */
  use_branch_tz_in_dashboards: boolean;
  /** Минимальный перерыв между мероприятиями одной команды, мин (для вида «Ресурсы»). */
  resource_break_minutes: number;
  /** Срок жизни «предброни», мин: сколько ждём предоплату после «Согласие получено». Дефолт 120. */
  prebooking_hold_minutes: number;
  /** Включено ли авто-аннулирование предброни (глобальный рубильник). Нет поля → включено. */
  prebooking_enabled?: boolean;
  /** Срок предброни для обычного клиента, мин. Нет поля → 120 (2 ч). */
  prebooking_hold_minutes_b2c?: number;
  /** Срок предброни для компании, мин. Нет поля → 4320 (72 ч). */
  prebooking_hold_minutes_b2b?: number;
  /** Анкета ОКК (опрос качества), редактируемая руководителем. Нет поля → дефолт-хардкод. */
  okk_questions?: OkkQuestionsConfig;
  bitrix: BitrixSettings;
  /** Временный рубильник супер-админа: снимает суточный/архивный замок карточек для всех
   *  (кнопка в профиле). Живёт отдельно от остальных настроек — см. PUT /ops/core-unlock. */
  core_unlock?: { enabled: boolean; since: number | null; by?: string | null };
}

// ── Палитра цветов ───────────────────────────────────────

export const PALETTE = [
  '#7F77DD', '#1D9E75', '#D85A30', '#D4537E', '#BA7517',
  '#378ADD', '#E24B4A', '#888780', '#639922', '#0EA5E9',
  '#A855F7', '#F97316', '#14B8A6', '#EAB308', '#EC4899', '#475569',
];
